import { BaseModel, ModelInstance, ToFrontHook, ToBackHook, DateField } from './util';
import { TournamentTeamUser } from './tournament-team-user';
import { Team } from './team';
import { TeamUser } from './team-user';
import { User } from './user';
import { TournamentTeam } from './tournament-team';
import { BaseStatistic, WinLoseType } from './base-statistic';
import { LeaguePlayer } from './league-player';

@ModelInstance({
  mappingFields: {
    league_player: 'leaguePlayer',
    tournament_team_user: 'tournamentTeamUser',
    team: 'team',
    team_user: 'teamUser',
    user: 'user',
    tournament_team: 'tournamentTeam',
    month: 'month',
    win_lose: 'winLose',
    games_count: 'gamesCount',
    won_games_count: 'wonGamesCount',
    team_score_sum: 'teamScoreSum',
    team_missed_sum: 'teamMissedSum',
    tournament_points: 'tournamentPoints',
    points: 'points',
    two_points_made: 'twoPointsMade',
    three_points_made: 'threePointsMade',
    free_throws_made: 'freeThrowsMade',
    one_points_made: 'onePointsMade',
    two_point_attempts: 'twoPointAttempts',
    three_point_attempts: 'threePointAttempts',
    free_throw_attempts: 'freeThrowAttempts',
    one_point_attempts: 'onePointAttempts',
    two_point_percent: 'twoPointPercent',
    three_point_percent: 'threePointPercent',
    free_throw_percent: 'freeThrowPercent',
    one_point_percent: 'onePointPercent',
    assists: 'assists',
    blocks: 'blocks',
    rebounds: 'rebounds',
    offensive_rebounds: 'offensiveRebounds',
    defensive_rebounds: 'defensiveRebounds',
    steals: 'steals',
    turnovers: 'turnovers',
    personal_fouls: 'personalFouls',
    technical_fouls: 'technicalFouls',
    unsportsmanlike_fouls: 'unsportsmanlikeFouls',
    disqualification_fouls: 'disqualificationFouls',
    drawn_fouls: 'drawnFouls',
    player_efficiency: 'playerEfficiency',
    shots_blocked: 'shotsBlocked',
    four_point_attempts: 'fourPointAttempts',
    four_points_made: 'fourPointsMade',
    four_point_percent: 'fourPointPercent',
    dunk_attempts: 'dunkAttempts',
    dunks_made: 'dunksMade',
    shootouts_won: 'shootoutsWon',
    shootouts_lost: 'shootoutsLost',
    shootouts_total: 'shootoutsTotal',
    shootout_won_percent: 'shootoutWonPercent',
    moneyball_free_throw_attempts: 'moneyballFreeThrowAttempts',
    moneyball_free_throws_made: 'moneyballFreeThrowsMade',
    moneyball_free_throw_percent: 'moneyballFreeThrowPercent',
    game_time: 'gameTime',
    newbie: 'newbie',
    rank: 'rank',
  },
  relation: {
    leaguePlayer: LeaguePlayer,
    tournamentTeamUser: TournamentTeamUser,
    team: Team,
    teamUser: TeamUser,
    user: User,
    tournamentTeam: TournamentTeam,
    month: DateField
  }
})
export class BasketballStatistic extends BaseModel implements BaseStatistic {
  leaguePlayer: LeaguePlayer;
  tournamentTeamUser: TournamentTeamUser;
  team: Team;
  teamUser: TeamUser;
  user: User;
  tournamentTeam: TournamentTeam;
  month: Date;
  winLose: WinLoseType;
  gamesCount: number;
  wonGamesCount: number;
  teamScoreSum: number;
  teamMissedSum: number;
  tournamentPoints: number;
  points: number;
  twoPointsMade: number;
  threePointsMade: number;
  freeThrowsMade: number;
  onePointsMade: number;
  twoPointAttempts: number;
  threePointAttempts: number;
  freeThrowAttempts: number;
  onePointAttempts: number;
  twoPointPercent: number;
  threePointPercent: number;
  freeThrowPercent: number;
  onePointPercent: number;
  assists: number;
  blocks: number;
  rebounds: number;
  offensiveRebounds: number;
  defensiveRebounds: number;
  steals: number;
  turnovers: number;
  personalFouls: number;
  technicalFouls: number;
  unsportsmanlikeFouls: number;
  disqualificationFouls: number;
  drawnFouls: number;
  playerEfficiency: number;
  shotsBlocked: number;
  fourPointAttempts: number;
  fourPointsMade: number;
  fourPointPercent: number;
  dunkAttempts: number;
  dunksMade: number;
  shootoutsWon: number;
  shootoutsLost: number;
  shootoutsTotal: number;
  shootoutWonPercent: number;
  moneyballFreeThrowAttempts: number;
  moneyballFreeThrowsMade: number;
  moneyballFreeThrowPercent: number;
  gameTime: number;
  newbie: boolean;
  rank: number;

  get totalFouls() {
    return Math.floor(10 * (
      (this.personalFouls || 0) +
      (this.technicalFouls || 0) +
      (this.unsportsmanlikeFouls || 0) +
      (this.disqualificationFouls || 0)
    )) / 10;
  }

  get gameMinutes(): number {
    return Math.floor(this.gameTime / 60);
  }

  get tournamentTeamPoints(): number {
    return this.tournamentTeam && this.tournamentTeam.points;
  }

  get onePointsPercent(): number {
    if (!this.onePointsMade || !this.onePointAttempts) {
      return 0;
    }
    return Math.round(1000 * this.onePointsMade / this.onePointAttempts) / 10;
  }

  get twoPointsPercent(): number {
    if (!this.twoPointsMade || !this.twoPointAttempts) {
      return 0;
    }
    return Math.round(1000 * this.twoPointsMade / this.twoPointAttempts) / 10;
  }

  get threePointsPercent(): number {
    if (!this.threePointsMade || !this.threePointAttempts) {
      return 0;
    }
    return Math.round(1000 * this.threePointsMade / this.threePointAttempts) / 10;
  }

  get freeThrowsPercent(): number {
    if (!this.freeThrowsMade || !this.freeThrowAttempts) {
      return 0;
    }
    return Math.round(1000 * this.freeThrowsMade / this.freeThrowAttempts) / 10;
  }

  get totalRebounds(): number {
    return Math.floor(10 * ((this.offensiveRebounds || 0) + (this.defensiveRebounds || 0))) / 10;
  }

  @ToFrontHook
  static toFront(data: any): any {}

  @ToBackHook
  static toBack(data: any): any {}
}
